import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LayersIcon from "@mui/icons-material/Layers";
import { Box, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { create } from "zustand";
import { useRampState } from "../../../../containers/RampStateContainer";
import { useSnackbar } from "../../../../containers/SnackbarContainer";
import { useUserPreferences } from "../../../../containers/UserPreferencesContainer";
import { ConfirmationDialog } from "../../../../widgets/ConfirmationDialog";
import { RampCanvasV2 } from "./RampCanvasV2/RampCanvasV2";
import { useAutoEgress } from "../RampEditor/hooks/useAutoEgress";
import {
  useLocalRampState,
  useRampEditorState,
  useSelectedToolState,
} from "../RampEditor/RampEditorPresenter";
import { LayerPanel } from "./LayerPanel";
import { FloatingPanel } from "./components/FloatingPanel";
import { BottomToolbar } from "./components/BottomToolbar";
import { usePanelState } from "./stores/usePanelState";
import { CompactToolbar } from "./components/CompactToolbar";
import { SettingsPanel } from "./components/SettingsPanel";
import { ViewControlsPanel } from "./components/ViewControlsPanel";
import { EditableRampName } from "./components/EditableRampName";
import { ReferenceAircraftDialog } from "../RampEditor/ReferenceAircraftDialog";
import { SetupWizard } from "./components/SetupWizard";
import { useSetupWizardStore } from "./components/SetupWizard/useSetupWizardStore";

// Store for Reference Aircraft dialog state
export const useReferenceAircraftDialogState = create<{
  open: boolean;
  setOpen: (open: boolean) => void;
}>((set) => ({
  open: false,
  setOpen: (open: boolean) => set({ open }),
}));

type Props = {
  onClose: () => void;
};

export const RampEditorV2Presenter: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ramp, setRamp } = useRampState();
  const {
    setRamp: setLocalRamp,
    ramp: localRamp,
    activeShapes,
    setActiveShapes,
    highlightedShapes,
    setHighlightedShapes,
  } = useLocalRampState();
  const { selectedTool, setSelectedTool } = useSelectedToolState();
  const snackbar = useSnackbar();
  const { preferences, setPreferences } = useUserPreferences();
  const { showGuideLines } = useRampEditorState();
  const { rightPanelVisible, toggleRightPanel } = usePanelState();
  const { open: referenceAircraftDialogOpen, setOpen: setReferenceAircraftDialogOpen } = useReferenceAircraftDialogState();
  const hasLoadedInitialPreferences = React.useRef(false);

  // Track panel width from localStorage for toggle button positioning
  const [panelWidth, setPanelWidth] = React.useState(() => {
    const saved = localStorage.getItem("ramp-editor-panel-width");
    if (saved) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 280 && parsed <= 400) return parsed;
    }
    return 300;
  });

  // Listen for storage changes (when FloatingPanel resizes)
  React.useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem("ramp-editor-panel-width");
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed >= 280 && parsed <= 400) {
          setPanelWidth(parsed);
        }
      }
    };
    window.addEventListener("storage", handleStorage);
    // Also poll since storage event doesn't fire in same tab
    const interval = setInterval(handleStorage, 500);
    return () => {
      window.removeEventListener("storage", handleStorage);
      clearInterval(interval);
    };
  }, []);

  // Setup wizard state
  const { isActive: isSetupWizardActive, startWizard, exitWizard } = useSetupWizardStore();
  const hasCheckedSetupMode = React.useRef(false);


  // Track if we've initialized local ramp
  const hasInitializedLocalRamp = React.useRef(false);

  React.useEffect(() => {
    // Only set local ramp ONCE on initial load
    // Don't overwrite local changes when ramp reference changes
    if (ramp && ramp.id && !hasInitializedLocalRamp.current) {
      hasInitializedLocalRamp.current = true;
      setLocalRamp(ramp);
    }
  }, [ramp, setLocalRamp]);

  // Check if ramp needs setup
  // Skip wizard for already-calibrated ramps:
  // - Old way: has valid dimensions (width > 0 && depth > 0), no GPS points needed
  // - New way: has valid dimensions + 3 GPS calibration points
  const needsSetup = React.useMemo(() => {
    if (!localRamp) return false;
    const hasValidDimensions = (localRamp.width ?? 0) > 0 && (localRamp.depth ?? 0) > 0;
    // If ramp has valid dimensions, it's already calibrated (skip wizard)
    // This supports both old-style (manual dimensions) and new-style (GPS calibration)
    return !hasValidDimensions;
  }, [localRamp?.width, localRamp?.depth]);

  // Auto-start setup wizard for new ramps that need calibration
  React.useEffect(() => {
    if (!hasCheckedSetupMode.current && localRamp && needsSetup) {
      hasCheckedSetupMode.current = true;
      startWizard();
    }
  }, [localRamp, needsSetup, startWizard]);

  // Load initial preference once
  React.useEffect(() => {
    if (
      !hasLoadedInitialPreferences.current &&
      preferences.showGuideLines !== undefined
    ) {
      useRampEditorState
        .getState()
        .setShowGuideLines(preferences.showGuideLines);
      hasLoadedInitialPreferences.current = true;
    }
  }, [preferences.showGuideLines]);

  // Save preference when it changes (but not on initial load)
  React.useEffect(() => {
    if (hasLoadedInitialPreferences.current) {
      setPreferences((currentPreferences) => ({
        ...currentPreferences,
        showGuideLines,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGuideLines]); // setPreferences is stable (useCallback with [])

  // Auto-calculate egress whenever shapes change (debounced)
  useAutoEgress();

  // Z-order keyboard shortcuts (Cmd+[/] and Cmd+Shift+[/])
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle bracket keys with modifier
      const isBracketLeft = e.code === "BracketLeft";
      const isBracketRight = e.code === "BracketRight";
      if (!isBracketLeft && !isBracketRight) return;

      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (!modKey) return;

      if (activeShapes.length !== 1) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      const shapeId = activeShapes[0];
      const shapes = localRamp?.shapes || [];
      const index = shapes.findIndex((s) => s.id === shapeId);
      if (index === -1) {
        return;
      }

      let newShapes = [...shapes];

      if (isBracketLeft && e.shiftKey) {
        // Send to back
        const [shape] = newShapes.splice(index, 1);
        newShapes.unshift(shape);
      } else if (isBracketRight && e.shiftKey) {
        // Bring to front
        const [shape] = newShapes.splice(index, 1);
        newShapes.push(shape);
      } else if (isBracketLeft && index > 0) {
        // Send backward
        [newShapes[index], newShapes[index - 1]] = [newShapes[index - 1], newShapes[index]];
      } else if (isBracketRight && index < shapes.length - 1) {
        // Bring forward
        [newShapes[index], newShapes[index + 1]] = [newShapes[index + 1], newShapes[index]];
      } else {
        return; // No change needed - already at boundary
      }

      // Apply sort_order to persist the new ordering
      const shapesWithSortOrder = newShapes.map((s, index) => ({ ...s, sort_order: index }));
      setLocalRamp((prev) => ({ ...prev, shapes: shapesWithSortOrder }));
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [activeShapes, localRamp?.shapes, setLocalRamp]);

  // Cmd+S to save
  React.useEffect(() => {
    const handleSave = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey && e.key === "s") {
        e.preventDefault();
        if (localRamp) {
          setRamp(localRamp);
          snackbar.notify({ text: t('settings.rampsSettings.rampEditor.saveSuccess'), severity: "success" });
        }
      }
    };
    window.addEventListener("keydown", handleSave);
    return () => window.removeEventListener("keydown", handleSave);
  }, [localRamp, setRamp, snackbar, t]);

  // State for exit confirmation dialog (Cancel button)
  const [showExitConfirmation, setShowExitConfirmation] = React.useState(false);

  // Check if there are unsaved changes by comparing localRamp with original ramp
  const hasUnsavedChanges = React.useMemo((): boolean => {
    if (!ramp || !localRamp) return false;

    // Compare shapes
    if (JSON.stringify(ramp.shapes) !== JSON.stringify(localRamp.shapes)) return true;

    // Compare markings
    if (JSON.stringify(ramp.markings) !== JSON.stringify(localRamp.markings)) return true;

    // Compare calibration_points
    if (JSON.stringify(ramp.calibration_points) !== JSON.stringify(localRamp.calibration_points)) return true;

    // Compare ramp properties
    if (ramp.name !== localRamp.name) return true;
    if (ramp.width !== localRamp.width) return true;
    if (ramp.depth !== localRamp.depth) return true;
    if (ramp.reference_image !== localRamp.reference_image) return true;

    // Compare layer_groups
    if (JSON.stringify(ramp.layer_groups) !== JSON.stringify(localRamp.layer_groups)) return true;

    return false;
  }, [ramp, localRamp]);

  // State for browser back button / trackpad swipe confirmation
  const [showBackConfirmation, setShowBackConfirmation] = React.useState(false);
  const isHandlingBack = React.useRef(false);

  // Block browser back button / swipe navigation when there are unsaved changes
  // Uses capture phase to intercept before HashRouter processes the event
  React.useEffect(() => {
    // Push a state to detect back navigation
    const currentHash = window.location.hash;
    window.history.pushState({ rampEditorGuard: true }, "", currentHash);

    const handlePopState = (event: PopStateEvent) => {
      if (isHandlingBack.current) return;

      // Check unsaved changes via Zustand store (avoids stale closure)
      const currentLocalRamp = useLocalRampState.getState().ramp;
      const currentRamp = ramp;

      const hasChanges = (() => {
        if (!currentRamp || !currentLocalRamp) return false;
        if (JSON.stringify(currentRamp.shapes) !== JSON.stringify(currentLocalRamp.shapes)) return true;
        if (JSON.stringify(currentRamp.markings) !== JSON.stringify(currentLocalRamp.markings)) return true;
        if (JSON.stringify(currentRamp.calibration_points) !== JSON.stringify(currentLocalRamp.calibration_points)) return true;
        if (currentRamp.name !== currentLocalRamp.name) return true;
        if (currentRamp.width !== currentLocalRamp.width) return true;
        if (currentRamp.depth !== currentLocalRamp.depth) return true;
        if (currentRamp.reference_image !== currentLocalRamp.reference_image) return true;
        if (JSON.stringify(currentRamp.layer_groups) !== JSON.stringify(currentLocalRamp.layer_groups)) return true;
        return false;
      })();

      if (hasChanges) {
        // Prevent HashRouter from processing this event
        event.stopImmediatePropagation();
        // Push state back to prevent navigation, then show dialog
        window.history.pushState({ rampEditorGuard: true }, "", currentHash);
        setShowBackConfirmation(true);
      }
      // If no changes, allow the back navigation to proceed naturally
    };

    // Use capture phase to run before HashRouter's listener
    window.addEventListener("popstate", handlePopState, true);
    return () => {
      window.removeEventListener("popstate", handlePopState, true);
    };
  }, [ramp]);

  // Handler for confirming back navigation
  const confirmBackNavigation = () => {
    isHandlingBack.current = true;
    setShowBackConfirmation(false);
    // Go back twice: once for the guard state we pushed, once for actual back
    window.history.go(-2);
  };

  // Handle page refresh / tab close with beforeunload
  React.useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Guarded exit handler for Cancel button and back arrow
  const handleExit = () => {
    if (hasUnsavedChanges) {
      setShowExitConfirmation(true);
    } else {
      navigate("/settings?tab=Ramps");
    }
  };

  const onClickSaveRamp = () => {
    setRamp(localRamp);
    snackbar.notify({ text: t('settings.rampsSettings.rampEditor.saveSuccess'), severity: "success" });
  };

  // State for centering canvas on a shape
  const [centerOnShapeId, setCenterOnShapeId] = React.useState<string | null>(null);

  // Handle centering and highlighting a shape from LayerPanel
  const handleCenterShape = React.useCallback((shapeId: string) => {
    // Brief highlight effect (0.75 seconds)
    setHighlightedShapes([shapeId]);
    setTimeout(() => setHighlightedShapes([]), 750);
    // Scroll canvas to center on shape
    setCenterOnShapeId(shapeId);
  }, [setHighlightedShapes]);

  // Setup wizard handlers
  const handleSetupComplete = React.useCallback(() => {
    snackbar.notify({
      text: t("settings.rampsSettings.setupWizard.complete", "Ramp calibration complete!"),
      severity: "success",
    });
  }, [snackbar, t]);

  const handleSetupCancel = React.useCallback(() => {
    // User cancelled setup - navigate back to ramps list
    navigate("/settings?tab=Ramps");
  }, [navigate]);

  if (!localRamp) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: "hidden",
        backgroundColor: "#1a1a2e",
      }}
    >
      {/* Full-screen canvas */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
        }}
      >
        <RampCanvasV2
          ramp={localRamp}
          setRamp={setLocalRamp}
          selectedTool={selectedTool}
          setSelectedTool={setSelectedTool}
          centerOnShapeId={centerOnShapeId}
          onCenterComplete={() => setCenterOnShapeId(null)}
        />
      </Box>

      {/* Top-left: Back button and ramp name - hidden during setup */}
      {!isSetupWizardActive && (
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1,
            py: 0.5,
            borderRadius: 2,
          }}
        >
          <Tooltip title="Back to ramps">
            <IconButton size="small" onClick={handleExit}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <EditableRampName
            name={localRamp?.name || "Ramp"}
            onNameChange={(newName) => {
              if (localRamp) {
                setLocalRamp({ ...localRamp, name: newName });
              }
            }}
          />
        </Paper>
      )}

      {/* Left side: Compact tool palette - hidden during setup */}
      {!isSetupWizardActive && (
        <Box
          sx={{
            position: "absolute",
            left: 16,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <CompactToolbar
            selectedTool={selectedTool}
            setSelectedTool={setSelectedTool}
            ramp={localRamp}
          />
          <ViewControlsPanel />
          <SettingsPanel />
        </Box>
      )}

      {/* Zoom controls only - visible during setup */}
      {isSetupWizardActive && (
        <Box
          sx={{
            position: "absolute",
            left: 16,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 10,
          }}
        >
          <ViewControlsPanel zoomOnly />
        </Box>
      )}

      {/* Right side: Layers panel toggle - hidden during setup */}
      {!isSetupWizardActive && (
        <Tooltip
          title={rightPanelVisible ? t("common.hide") : "Show Layers"}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: "grey.800",
                color: "white",
                fontSize: "0.75rem",
                fontWeight: 500,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                boxShadow: 3,
              },
            },
          }}
        >
          <IconButton
            onClick={toggleRightPanel}
            sx={{
              position: "absolute",
              right: rightPanelVisible ? panelWidth + 32 : 16,
              top: 16,
              zIndex: 11,
              backgroundColor: "background.paper",
              boxShadow: 2,
              transition: rightPanelVisible ? "none" : "right 0.2s ease",
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            <LayersIcon />
          </IconButton>
        </Tooltip>
      )}

      {/* Floating Right Panel - Layers - hidden during setup */}
      {!isSetupWizardActive && (
        <FloatingPanel position="right" visible={rightPanelVisible} width={300} resizable>
          <LayerPanel
            ramp={localRamp}
            setRamp={setLocalRamp}
            activeShapes={activeShapes}
            setActiveShapes={setActiveShapes}
            highlightedShapes={highlightedShapes}
            onClose={toggleRightPanel}
            onCenterShape={handleCenterShape}
          />
        </FloatingPanel>
      )}

      {/* Bottom Toolbar - Save Controls - hidden during setup */}
      {!isSetupWizardActive && (
        <BottomToolbar
          onSave={onClickSaveRamp}
          onCancel={handleExit}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      )}

      {/* Confirmation dialog for Cancel button / back arrow */}
      {showExitConfirmation && (
        <ConfirmationDialog
          title={t("settings.rampsSettings.rampEditor.unsavedChanges.title")}
          text={t("settings.rampsSettings.rampEditor.unsavedChanges.message")}
          okText={t("settings.rampsSettings.rampEditor.unsavedChanges.exitButton")}
          cancelText={t("settings.rampsSettings.rampEditor.unsavedChanges.stayButton")}
          onOk={() => {
            setShowExitConfirmation(false);
            navigate("/settings?tab=Ramps");
          }}
          onCancel={() => setShowExitConfirmation(false)}
          onClose={() => setShowExitConfirmation(false)}
        />
      )}

      {/* Confirmation dialog for browser back button / trackpad swipe */}
      {showBackConfirmation && (
        <ConfirmationDialog
          title={t("settings.rampsSettings.rampEditor.unsavedChanges.title")}
          text={t("settings.rampsSettings.rampEditor.unsavedChanges.message")}
          okText={t("settings.rampsSettings.rampEditor.unsavedChanges.exitButton")}
          cancelText={t("settings.rampsSettings.rampEditor.unsavedChanges.stayButton")}
          onOk={confirmBackNavigation}
          onCancel={() => setShowBackConfirmation(false)}
          onClose={() => setShowBackConfirmation(false)}
        />
      )}

      {/* Reference Aircraft dialog */}
      <ReferenceAircraftDialog
        open={referenceAircraftDialogOpen}
        onClose={() => setReferenceAircraftDialogOpen(false)}
        rampWidth={localRamp?.width ?? 100}
        rampDepth={localRamp?.depth ?? 100}
      />

      {/* Setup Wizard overlay */}
      {isSetupWizardActive && (
        <SetupWizard
          ramp={localRamp}
          setRamp={setLocalRamp}
          onComplete={handleSetupComplete}
          onCancel={handleSetupCancel}
          onSave={setRamp}
        />
      )}
    </Box>
  );
};
